import React from 'react'
import clsx from 'clsx'
import { RangeSlider as RangeSliderStyles, Tooltip as TooltipStyles } from '@airbus/elements/elements'
import { createUseStyles } from '../../lib/styling'
import useControlled from '../../lib/hooks/useControlled'
import useDeprecatedComponent from '../../lib/hooks/useDeprecatedComponent'
import useDeprecatedProps from '../../lib/hooks/useDeprecatedProps'
import useGetLatest from '../../lib/hooks/useGetLatest'
import Popper, { PopperHandle } from '../Popper'
import { CommonProps } from '../helperTypes'

// TODO with inputs mode
// FIXME click to move the nearest thumb should be limited to track/segments

type Props = {
  /** Array of aria-label for screen-readers to apply to the thumbs */
  ariaLabel?: string[]
  /** Array of aria-labelledby for screen-readers to apply to the thumbs */
  ariaLabelledby?: string[]
  /**
   * Determines the initial positions of the thumbs and the number of thumbs.<br />
   * Each value will determine the position of one thumb. The values in the array must be sorted.
   * @default [0]
   */
  defaultValue?: number[]
  /** disable the whole component */
  disabled?: boolean
  /** Function that return a custom string to display ticks value */
  getTickLabel?: (val: number) => string
  /** Function that return a custom string to display thumbs value for screen-readers and tooltips */
  getThumbLabel?: (index: number, val: number) => string
  /**
   * The maximum value of the slider
   * @default 100
   */
  max?: number
  /**
   * The minimum value of the slider
   * @default 0
   */
  min?: number
  /**
   * The minimum distance between any pair of thumbs.<br />
   * Default to step prop value if not specified.<br />
   * Set to 0 to allow thumbs to cross.<br />
   * Should be evenly divisible by the step value.
   */
  minDistance?: number
  /** Called when the value of the slider has changed */
  onChange?: (value: number[]) => void
  /** Called at each update (caution: high-volume updates when dragging) */
  onUpdate?: (value: number[]) => void
  /**
   * Display size of the thumb, by default the value is medium
   * @default "medium"
   */
  size?: 'medium' | 'large'
  /**
   * Value to be added or subtracted on each step the slider makes.<br />
   * Must be greater than zero.<br />
   * `max` and `min` should be evenly divisible by the step value.
   * @default 1
   */
  step?: number
  /**
   * Display ticks with bottom labels.<br />
   * A number will display ticks by dividing the range equally.<br />
   * An array will display ticks at specified values.
   * @default 0
   */
  ticks?: number | number[]
  /** Like defaultValue but for [controlled components](https://reactjs.org/docs/forms.html#controlled-components) */
  value?: number[]
  /** Set to true to display a vertical slider */
  vertical?: boolean
}

type DeprecatedProps = {
  domain?: { min: number; max: number }
  handleViewValue?: boolean
  mode?: string
  onSlideEnd?: (value: number[]) => void
  onSlideStart?: (value: number[]) => void
  reversed?: boolean
  values?: number[]
}

export type RangeSliderProps = Props & DeprecatedProps & CommonProps

const handleDeprecatedProps = (props: RangeSliderProps) => {
  const { domain, handleViewValue, max, min, value, values, ...rest } = props
  return {
    max: domain ? domain.max : max,
    min: domain ? domain.min : min,
    value: values ? values : value,
    ...rest,
  }
}

const checkProps = ({
  defaultValue = [0],
  min = 0,
  minDistance,
  max = 100,
  step = 1,
  value,
  size = 'medium',
}: RangeSliderProps): void => {
  if (process.env.NODE_ENV === 'production') return
  const isInRange = (vals: number[]) => vals.reduce((acc, val) => val >= min && val <= max, true)
  if (defaultValue && !isInRange(defaultValue)) {
    console.warn(
      [
        '[AIRBUS-DS] The RangeSlider expects the `defaultValue` prop to be contained in `min` and `max` range.',
        `However, [${defaultValue}] was provided.`,
      ].join('\n'),
    )
  }
  if (value && !isInRange(value)) {
    console.warn(
      [
        '[AIRBUS-DS] The RangeSlider expects the `value` prop to be contained in `min` and `max` range.',
        `However, [${value}] was provided.`,
      ].join('\n'),
    )
  }
  if (step < 0) {
    console.warn(
      [
        '[AIRBUS-DS] The RangeSlider expects the `step` prop to be greater than zero.',
        `However, "${step}" was provided.`,
      ].join('\n'),
    )
  }
  if (((max - min) * 100) % (step * 100) !== 0) {
    console.warn(
      [
        '[AIRBUS-DS] The RangeSlider expects the `min` and `max` range to be evenly divisible by the `step` prop.',
        `However, "${step}" was provided.`,
      ].join('\n'),
    )
  }
  if (minDistance) {
    if (minDistance < 0) {
      console.warn(
        [
          '[AIRBUS-DS] The RangeSlider expects the `minDistance` prop to be greater than zero.',
          `However, "${minDistance}" was provided.`,
        ].join('\n'),
      )
    }
    if (minDistance % step !== 0) {
      console.warn(
        [
          '[AIRBUS-DS] The RangeSlider expects the `minDistance` prop to be evenly divisible by the `step` prop.',
          `However, "${minDistance}" was provided.`,
        ].join('\n'),
      )
    }
  }
  if (!['large', 'medium'].includes(size)) {
    console.warn(
      [
        "[AIRBUS-DS] The RangeSlider expects the `size` prop to either be 'medium' or 'large'.",
        `However, "${size}" was provided`,
      ].join('\n'),
    )
  }
}

// generic helpers
const getBoundingClientRect = (element: Element) => {
  const rect = element.getBoundingClientRect()
  return {
    bottom: Math.ceil(rect.bottom),
    left: Math.ceil(rect.left),
    top: Math.ceil(rect.top),
    width: Math.ceil(rect.width),
    height: Math.ceil(rect.height),
  }
}

const sortNumList = (arr: number[]) => [...arr].sort((a, b) => Number(a) - Number(b))

const getValueForClientX = (
  element: Element | null,
  clientX: number,
  clientY: number,
  min: number,
  max: number,
  allowOutside: boolean = false,
) => {
  if (!element) return 0
  const { left, top, width } = getBoundingClientRect(element)
  if (!allowOutside && (clientY < top || clientY > top + 8)) return -1
  const percentageValue = (clientX - left) / width
  const val = (max - min) * percentageValue
  return val + min
}
const getValueForClientY = (
  element: Element | null,
  clientX: number,
  clientY: number,
  min: number,
  max: number,
  allowOutside: boolean = false,
) => {
  if (!element) return 0
  const { bottom, height, left } = getBoundingClientRect(element)
  if (!allowOutside && (clientX < left || clientX > left + 8)) return -1
  const percentageValue = (bottom - clientY) / height
  const val = (max - min) * percentageValue
  return val + min
}
const roundToStep = (val: number, min: number, max: number, step: number) => {
  let left = min
  let right = max
  while (left < val && left + step < val) {
    left += step
  }
  right = Math.min(left + step, max)

  if (val - left < right - val) {
    return left
  }
  return right
}
const getPercentageForValue = (val: number, min: number, max: number) =>
  Math.max(0, Math.min(100, ((val - min) / (max - min)) * 100))

const getTickValues = (count: number, min: number, max: number) => {
  const tickSize = (max - min) / (count - 1)
  let ticks = [min]
  while (ticks[ticks.length - 1] < max - tickSize) {
    ticks.push(ticks[ticks.length - 1] + tickSize)
  }
  if (count > 1) ticks.push(max)
  return ticks
}

const useStyles = createUseStyles(RangeSliderStyles, 'rangeslider')
const useTooltipStyles = createUseStyles(TooltipStyles, 'rangeslider-tooltip')

const RangeSlider = (props: RangeSliderProps) => {
  const {
    ariaLabel,
    ariaLabelledby,
    className,
    defaultValue = [0],
    disabled,
    getThumbLabel,
    getTickLabel,
    max = 100,
    min = 0,
    minDistance: minDistanceProp,
    onChange,
    onUpdate,
    step = 1,
    ticks: ticksProp = 0,
    value: valueProp,
    size = 'medium',
    vertical,
    ...rest
  } = handleDeprecatedProps(props)
  useDeprecatedProps(
    'RangeSlider',
    {
      domain: 'min/max',
      mode: 'minDistance',
      onSlideStart: 'onUpdate',
      onSlideEnd: 'onChange',
      reversed: null,
      values: 'value',
    },
    props,
  )
  React.useEffect(() => checkProps(props), [props])
  const minDistance = React.useMemo(() => {
    if (minDistanceProp !== undefined && minDistanceProp >= 0) return minDistanceProp
    else return step
  }, [minDistanceProp, step])
  const classes = useStyles()
  const tooltipClasses = useTooltipStyles()

  // refs
  const rootRef = React.useRef<HTMLDivElement>(null)
  const thumbsRefs = React.useRef<HTMLSpanElement[]>([])
  const popperRef = React.useRef<PopperHandle>(null)

  // controlled / uncontrolled value
  const [value, setValue] = useControlled({
    value: valueProp,
    defaultValue: defaultValue,
  })
  // temporary value for thumb dragging
  const [tempValue, setTempValue] = React.useState<number[] | null>()
  // latest ref hook pattern to reduce new closure creation on each render
  const getLatest = useGetLatest({
    getThumbLabel,
    onChange,
    onUpdate,
    value,
    tempValue,
  })
  // helper to update/dispatch value change at thumb index
  const setValueAtIndex = React.useCallback(
    (index: number, val: number, temp?: boolean) => {
      const { onChange, onUpdate } = getLatest()
      const result = sortNumList([...value.slice(0, index), val, ...value.slice(index + 1)])
      if (temp) {
        // temporary change (thumb drag)
        setTempValue(result)
        onUpdate && onUpdate(result)
      } else {
        // final value change
        setValue(result)
        onChange && onChange(result)
      }
    },
    [getLatest, setValue, value],
  )

  // states
  const [activeThumbIndex, setActiveThumbIndex] = React.useState<number>(-1)
  const [focusedThumbIndex, setFocusedThumbIndex] = React.useState<number>(-1)
  const [isDragging, setIsDragging] = React.useState<boolean>(false)

  // mouse / keyboard events
  const handleThumbMouseOver = React.useCallback((index: number) => {
    setActiveThumbIndex(index)
  }, [])
  const handleThumbMouseLeave = React.useCallback(() => {
    const { tempValue } = getLatest()
    if (!tempValue) setActiveThumbIndex(-1)
  }, [getLatest])
  const handleThumbPress = React.useCallback(
    (pressEvt: any, index: number) => {
      const handleDrag = (evt: any) => {
        const { tempValue } = getLatest()
        let newValue
        const clientX = evt.type === 'touchmove' ? evt.changedTouches[0].clientX : evt.clientX
        const clientY = evt.type === 'touchmove' ? evt.changedTouches[0].clientY : evt.clientY
        if (!vertical) {
          newValue = getValueForClientX(rootRef.current, clientX, clientY, min, max, true)
        } else {
          newValue = getValueForClientY(rootRef.current, clientX, clientY, min, max, true)
        }
        let newRoundedValue = roundToStep(newValue, min, max, step)
        // apply minDistance if needed
        if (tempValue && tempValue.length > 1 && minDistance > 0) {
          if (index > 0) {
            const minValue = tempValue[index - 1] + minDistance
            if (newRoundedValue < minValue) newRoundedValue = minValue
          }
          if (index < tempValue.length - 1) {
            const maxValue = tempValue[index + 1] - minDistance
            if (newRoundedValue > maxValue) newRoundedValue = maxValue
          }
        }
        setValueAtIndex(index, newRoundedValue, true)
        if (popperRef.current && popperRef.current.update) {
          popperRef.current.update()
        }
      }
      const handleRelease = () => {
        const { tempValue, value, onChange } = getLatest()
        document.removeEventListener('mousemove', handleDrag)
        document.removeEventListener('touchmove', handleDrag)
        document.removeEventListener('mouseup', handleRelease)
        document.removeEventListener('touchend', handleRelease)
        const sortedValue = sortNumList(tempValue || value)
        setValue(sortedValue)
        setTempValue(null)
        setActiveThumbIndex(-1)
        setIsDragging(false)
        onChange && onChange(sortedValue)
      }

      pressEvt.stopPropagation()
      setActiveThumbIndex(index)
      setIsDragging(true)
      document.addEventListener('mousemove', handleDrag)
      document.addEventListener('touchmove', handleDrag)
      document.addEventListener('mouseup', handleRelease)
      document.addEventListener('touchend', handleRelease)
    },
    [getLatest, max, min, minDistance, setTempValue, setValue, setValueAtIndex, step, vertical],
  )
  const handleTrackPress = React.useCallback(
    (evt: any) => {
      const { value } = getLatest()
      let newValue
      const clientX = evt.type === 'touchstart' ? evt.touches[0].clientX : evt.clientX
      const clientY = evt.type === 'touchstart' ? evt.touches[0].clientY : evt.clientY
      if (!vertical) {
        newValue = getValueForClientX(rootRef.current, clientX, clientY, min, max)
      } else {
        newValue = getValueForClientY(rootRef.current, clientX, clientY, min, max)
      }
      if (newValue < 0) return

      // snap nearest thumb to the position
      const newRoundedValue = roundToStep(newValue, min, max, step)
      let nearestIndex = 0
      if (value.length > 1) {
        let minDiff = Math.abs(newRoundedValue - value[0])
        value.forEach((val: number, index: number) => {
          const diff = Math.abs(newRoundedValue - val)
          if (minDiff > diff) {
            minDiff = diff
            nearestIndex = index
          }
        })
      }
      setValueAtIndex(nearestIndex, newRoundedValue)
    },
    [getLatest, max, min, setValueAtIndex, step, vertical],
  )
  const handleKeyDownCapture = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      const managedKeys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End']
      const { key } = event.nativeEvent
      if (!managedKeys.includes(key) || focusedThumbIndex < 0) return
      event.preventDefault()

      const { value } = getLatest()
      let val = value[focusedThumbIndex]
      switch (key) {
        default:
        case 'ArrowDown':
        case 'ArrowLeft':
          val = Math.max(val - step, min)
          break
        case 'ArrowUp':
        case 'ArrowRight':
          val = Math.min(val + step, max)
          break
        case 'Home':
          val = min
          break
        case 'End':
          val = max
          break
      }
      setValueAtIndex(focusedThumbIndex, val)
    },
    [focusedThumbIndex, getLatest, max, min, step, setValueAtIndex],
  )

  // props getters
  const segments = React.useMemo(() => {
    const sortedValues = sortNumList(tempValue || value)
    return [...sortedValues, max].map((val, index) => ({
      val,
      getSegmentProps: () => {
        const percent = getPercentageForValue(sortedValues[index - 1] ? sortedValues[index - 1] : min, min, max)
        const size = getPercentageForValue(val, min, max) - percent
        const show = index === sortedValues.length - 1 || (index > 0 && index < sortedValues.length - 1)
        return {
          key: `track-${index}`,
          className: classes.track,
          style: {
            display: show ? 'block' : 'none',
            ...(!vertical && {
              left: `${percent}%`,
              width: `${size}%`,
            }),
            ...(vertical && {
              top: `${100 - percent - size}%`,
              height: `${size}%`,
            }),
          },
        }
      },
    }))
  }, [classes, max, min, tempValue, value, vertical])

  const ticks = React.useMemo(() => {
    if (ticksProp === 0) return []
    const sortedValues = sortNumList(tempValue || value)
    const arrTicks = Array.isArray(ticksProp) ? ticksProp : getTickValues(ticksProp as number, min, max)
    return arrTicks.map((val, index) => ({
      val: val !== Math.round(val) ? Number(val.toFixed(1)) : val,
      getTickProps: () => ({
        key: `tick-${index}`,
        className: clsx(classes.tick, {
          [classes.tick_start]: val === min,
          [classes.tick_end]: val === max,
          [classes.tick_current]: val !== min && val !== max && sortedValues.indexOf(val) >= 0,
          [classes.tick_outside]:
            (sortedValues.length === 1 && val > sortedValues[0]) ||
            (sortedValues.length > 1 && (val < sortedValues[0] || val > sortedValues[sortedValues.length - 1])),
        }),
        style: {
          ...(!vertical && {
            left: `${getPercentageForValue(val, min, max)}%`,
          }),
          ...(vertical && {
            top: `${100 - getPercentageForValue(val, min, max)}%`,
          }),
        },
      }),
    }))
  }, [classes, max, min, tempValue, ticksProp, value, vertical])

  const getThumbAriaAttrs = React.useCallback(
    (val: number, index: number) => {
      const { getThumbLabel } = getLatest()
      const attrs: any = {
        'aria-valuemin': min,
        'aria-valuemax': max,
        'aria-valuenow': val,
        'aria-valuetext': getThumbLabel ? getThumbLabel(index, val) : val,
        role: 'slider',
      }
      if (ariaLabelledby) {
        attrs['aria-labelledby'] = ariaLabelledby[index]
      } else {
        let defaultAriaLabel = 'Select value'
        if (value.length === 2) defaultAriaLabel = index === 0 ? 'Select min' : 'Select max'
        else if (value.length > 2) defaultAriaLabel = `Select value ${index + 1}`
        attrs['aria-label'] = ariaLabel ? ariaLabel[index] : defaultAriaLabel
      }
      return attrs
    },
    [ariaLabel, ariaLabelledby, getLatest, max, min, value],
  )
  const thumbs = React.useMemo(
    () =>
      [...(tempValue || value)].map((val: number, index: number) => {
        const percent = getPercentageForValue(val, min, max)
        return {
          val,
          getThumbProps: () => ({
            key: `thumb-${index}`,
            className: classes.thumb,
            style: {
              ...(!vertical && {
                left: `clamp(0.25rem, ${percent}%, calc(100% - 0.25rem))`,
              }),
              ...(vertical && {
                top: `clamp(0.25rem, ${100 - percent}%, calc(100% - 0.25rem))`,
              }),
              cursor: isDragging && activeThumbIndex === index ? 'grabbing' : 'grab',
            },
            tabIndex: disabled ? -1 : 0,
            ...getThumbAriaAttrs(val, index),
            // events
            onFocus: () => setFocusedThumbIndex(index),
            onBlur: () => setFocusedThumbIndex(-1),
            onMouseDown: (evt: any) => handleThumbPress(evt, index),
            onTouchStart: (evt: any) => handleThumbPress(evt, index),
            onMouseLeave: () => handleThumbMouseLeave(),
            onMouseOut: () => handleThumbMouseLeave(),
            onMouseOver: () => handleThumbMouseOver(index),
          }),
        }
      }),
    [
      activeThumbIndex,
      classes,
      disabled,
      getThumbAriaAttrs,
      handleThumbMouseLeave,
      handleThumbMouseOver,
      handleThumbPress,
      isDragging,
      max,
      min,
      tempValue,
      value,
      vertical,
    ],
  )

  return (
    <div
      className={clsx(classes.root, className, {
        [classes._vertical]: vertical,
        [classes._disabled]: disabled,
        [classes._withticks]: ticks.length > 0,
        [classes[`_${size}`]]: size,
      })}
      ref={rootRef}
      onKeyDownCapture={handleKeyDownCapture}
      onMouseDown={disabled ? undefined : handleTrackPress}
      onTouchStart={handleTrackPress}
      {...rest}
    >
      {segments.map(({ getSegmentProps }) => (
        <div {...getSegmentProps()}></div>
      ))}
      {ticks.map(({ getTickProps, val }) => (
        <div {...getTickProps()}>{getTickLabel ? getTickLabel(val) : val}</div>
      ))}
      {thumbs.map(({ getThumbProps, val }, index) => (
        <div {...getThumbProps()}>
          <span
            ref={(el: HTMLSpanElement) => (thumbsRefs.current[index] = el)}
            style={{
              display: 'block',
              height: size === 'large' ? '2rem' : '1rem',
              width: size === 'large' ? '2rem' : '1rem',
              margin: size === 'large' ? '-3px 0 0 -3px' : '-2px 0 0 -2px',
            }}
          ></span>
        </div>
      ))}
      {activeThumbIndex >= 0 && (
        <Popper
          ref={popperRef}
          role='tooltip'
          className={tooltipClasses.root}
          arrowClassName={tooltipClasses.arrow}
          arrowSize={4}
          referenceNode={thumbsRefs.current[activeThumbIndex]}
          open
          placement={vertical ? 'left' : 'top'}
        >
          {getThumbLabel
            ? getThumbLabel(activeThumbIndex, (tempValue || value)[activeThumbIndex])
            : (tempValue || value)[activeThumbIndex]}
        </Popper>
      )}
    </div>
  )
}

export default RangeSlider

// retro compatiblity
export const Slider = (props: RangeSliderProps) => {
  useDeprecatedComponent('Slider', 'Please use RangeSlider.')
  return <RangeSlider {...props} />
}
